/**
 * Which machines, and on each which phones — shared by the Distribute dialog and the post editor.
 *
 * Ticking a machine means **every phone online there at dispatch**, resolved by the service, not by us,
 * because a phone that comes online between this choice and the click should get the clip and one that
 * went offline should not fail the push. Narrowing to specific phones is the exception, so it takes a
 * second click.
 */

import type { Device, DistributeTarget, Hub } from '../../api/types'
import { useT } from '../../i18n'
import { Badge, Empty, Pill } from '../../ui/components'
import { PhoneIcon } from '../../ui/PhoneIcon'
import { phoneName } from '../devices/phoneName'
import { useDirectionName, useDirections } from '../marketing/directions'

export type Pick = 'all' | Set<string>
export type Picked = Record<string, Pick | undefined>

/** The service's shape: `device_ids: null` = every phone online on that machine at dispatch — of the
 *  video's direction (2026-10-12: a video goes only to phones of its direction). */
export function toTargets(picked: Picked): DistributeTarget[] {
  return Object.entries(picked)
    .filter(([, v]) => v)
    .map(([hub_id, v]) => ({ hub_id, device_ids: v === 'all' ? null : Array.from(v as Set<string>) }))
}

/** A saved target list back into the picker's state. */
export function fromTargets(targets: DistributeTarget[]): Picked {
  const picked: Picked = {}
  for (const t of targets) picked[t.hub_id] = t.device_ids === null ? 'all' : new Set(t.device_ids)
  return picked
}

/** How many phones this reaches, counted the way the service resolves `null` — every online one of the
 *  direction, when one is given. */
export function countPhones(picked: Picked, devices: Device[], direction?: string | null): number {
  return Object.entries(picked).reduce((n, [hub, v]) => {
    if (!v) return n
    if (v instanceof Set) return n + v.size
    return n + devices.filter((d) => d.hub_id === hub && d.status !== 'offline' && (!direction || d.direction_id === direction)).length
  }, 0)
}

export function TargetPicker({
  hubs,
  devices,
  picked,
  onChange,
  direction,
}: {
  hubs: Hub[] | null
  devices: Device[]
  picked: Picked
  onChange: (next: Picked) => void
  /** the video's direction: only its phones can be picked; the others show whose they are */
  direction?: string | null
}) {
  const t = useT()
  const dirs = useDirections()
  const nameOf = useDirectionName()
  const ours = (d: Device) => !direction || d.direction_id === direction

  const toggleHub = (id: string) => onChange({ ...picked, [id]: picked[id] ? undefined : 'all' })
  const toggleDevice = (hub: string, dev: string, online: string[]) => {
    const cur = picked[hub] === 'all' ? new Set(online) : new Set(picked[hub] ?? [])
    if (cur.has(dev)) cur.delete(dev)
    else cur.add(dev)
    const next: Pick | undefined = cur.size === 0 ? undefined : cur.size === online.length ? 'all' : cur
    onChange({ ...picked, [hub]: next })
  }

  return (
    <>
      {hubs === null && <Empty>{t.distribute.loading}</Empty>}
      {hubs?.length === 0 && <Empty>{t.machines.none}</Empty>}
      {hubs?.map((hub) => {
        const devs = devices.filter((d) => d.hub_id === hub.hub_id)
        const online = devs.filter((d) => d.status !== 'offline' && ours(d)).map((d) => d.device_id)
        const sel = picked[hub.hub_id]
        return (
          <div key={hub.hub_id} className="panel">
            <label className={`panel-row${hub.online ? '' : ' dim'}`} style={{ cursor: hub.online ? 'pointer' : 'not-allowed' }}>
              <input type="checkbox" disabled={!hub.online || online.length === 0} checked={Boolean(sel)} onChange={() => toggleHub(hub.hub_id)} />
              <b>{hub.hub_id}</b>
              <span className="dim small">
                {hub.point} · {t.distribute.phonesOnline(online.length, devs.length)}
              </span>
              {!hub.online && <Badge kind="bad">{t.machines.offline}</Badge>}
              <span className="grow" />
              {sel === 'all' && <Pill>{t.distribute.allOnline}</Pill>}
              {sel instanceof Set && <Pill>{t.distribute.someOf(sel.size, online.length)}</Pill>}
            </label>
            {sel && (
              <div className="phone-choices">
                {devs.map((d) => (
                  <label key={d.device_id} className={`phone-choice${d.status === 'offline' || !ours(d) ? '' : ' is-on'}${ours(d) ? '' : ' other-direction'}`}
                         title={ours(d) ? undefined : t.distribute.otherDirection}>
                    <input
                      type="checkbox"
                      disabled={d.status === 'offline' || !ours(d)}
                      checked={ours(d) && (sel === 'all' ? d.status !== 'offline' : sel.has(d.device_id))}
                      onChange={() => toggleDevice(hub.hub_id, d.device_id, online)}
                    />
                    <PhoneIcon status={d.status} size={14} title={d.status} />
                    <span>{phoneName(d)}</span>
                    {direction && !ours(d) && (
                      <span className="tiny dim">{d.direction_id ? nameOf(dirs.byId.get(d.direction_id) ?? { direction_id: d.direction_id, name: d.direction_id, color: '' }) : t.distribute.noDirectionPhone}</span>
                    )}
                    {d.tiktok_account && <span className="dim tiny mono">@{d.tiktok_account.replace(/^@/, '')}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}
